import { push } from "connected-react-router";
import { call, delay, put, retry, take, takeEvery } from "redux-saga/effects";

import * as Sentry from "@sentry/react";
import * as userActions from "@store/user/userActions";
import { PaymentProvider } from "@types-runway/billing";
import { Case } from "@types-runway/case";
import { selectState } from "@store/selectState";
import { ErrorEvents, RootEntity } from "@types-runway/state";
import { Address, User } from "@types-runway/user";
import {
  PAYMENT_PROVIDER_STRIPE,
  PAYMENT_PROVIDER_WITHFLEX,
  VISIT_FEE_STRIPE_PRICE_ID,
  VISIT_FEE_STRIPE_PRODUCT_ID,
} from "@utils/constantsEnv";
import { fnv1a, toastSuccess } from "@utils/utils";

import { extractLabExamRequirements } from "@utils/labExam/labExam";
import {
  PaymentAttemptOutcome,
  PaymentAttemptStatus,
  PaymentRetryError,
  PaymentRetryErrorKind,
} from "@types-runway/paymentRetry";

import { classifyPaymentRetryError } from "./classifyPaymentRetryError";

import { cases, labExam, order, user } from "../../api";
import * as casesActions from "./casesActions";
import * as casesSlice from "./casesSlice";

interface InvoiceRequestPayload {
  caseId: number;
  pricing: {
    priceId: string;
    productId: string;
    provider: PaymentProvider;
  };
  promoCodeId?: string;
  customerId?: string;
  successUrl?: string;
  cancelUrl?: string;
}

/**
 * Reusable helper: attempts to create a case, retrying transient errors
 * up to 3 times with a 3s delay between attempts (4 total attempts).
 * Throws on exhaustion or on non-retryable errors (e.g. 401).
 */
export function* createCaseWithRetry(newCase: Case) {
  const questionsFingerprint = fnv1a(newCase.case_questions.toString());
  const payload = { case: { ...newCase, questionsFingerprint } };
  const MAX_ATTEMPTS = 4; // 1 initial + 3 retries
  const RETRY_DELAY_MS = 3000;

  // Manual retry loop instead of redux-saga's `retry` effect because `retry`
  // retries on ALL errors — we need to bail immediately on 401 (not transient).
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const { data } = yield call(() => cases.casePost("", payload));
      return data;
    } catch (error) {
      const exception: any = error;
      // Never retry on 401 — access denied is not transient.
      if (exception?.response?.status === 401) {
        throw error;
      }
      // If we've exhausted all attempts, throw the last error.
      if (attempt === MAX_ATTEMPTS - 1) {
        throw error;
      }
      // Wait before next retry.
      yield delay(RETRY_DELAY_MS);
    }
  }

  // Unreachable: loop always returns or throws. Guard against future refactors.
  throw new Error("createCaseWithRetry: exhausted all attempts without resolving");
}

function* createCase({ newCase }: casesActions.CreateCase) {
  yield put(casesSlice.newCaseFailure({}));
  yield put(casesSlice.newCasePending());

  try {
    const data: Case = yield call(createCaseWithRetry, newCase);
    yield put(casesSlice.newCaseSuccess(data));
    yield put(
      casesSlice.addCaseToFeed([
        {
          isPending: false,
          data,
        },
      ])
    );
  } catch (error) {
    const exception: any = error;
    const event = exception?.response?.status === 401 ? ErrorEvents.ACCESS_DENIED : undefined;
    const message =
      event || exception?.response?.statusText || exception?.message || "Unknown error.";
    yield put(
      casesSlice.newCaseFailure({
        event,
        message,
      })
    );
  }
}

// Validates shipping address against user state.
// Returns the validated address or null if no shipping address update is needed.
function getValidatedShippingAddress(addressId: string, user: User): Address | null {
  if (!addressId) {
    return null;
  }

  // Validate the address id against the user state.
  const shippingAddress = user.addresses.find((address) => address.id === addressId);

  if (!shippingAddress?.id) {
    throw new Error("UX Error: Can't update case shipping address state");
  }

  return shippingAddress;
}

/**
 * Compensates for a failed case creation by creating the case now.
 * Updates Redux state and the cases feed (with deduplication).
 * Returns the new caseId on success, or 0 if all attempts fail
 * (dispatches newCaseFailure and reports to Sentry on failure).
 *
 * Note: relies on the invariant that newCaseFailure does not clear
 * newCase.data — it only writes to the error field, preserving the case payload.
 */
function* compensateOrphanedCase(existingCase: Case) {
  try {
    const data: Case = yield call(createCaseWithRetry, existingCase);
    if (!data.id) {
      throw new Error("Case creation succeeded but returned no id");
    }

    yield put(casesSlice.newCaseSuccess(data));

    // Guard against duplicate feed entries if concurrent updateCase sagas
    // both enter compensation (possible with takeEvery).
    const feedCases: RootEntity<Case>[] = yield selectState<RootEntity<Case>[]>(
      (state) => state.case.cases.data
    );
    const alreadyInFeed = feedCases.some((c) => c.data.id === data.id);
    if (!alreadyInFeed) {
      yield put(casesSlice.addCaseToFeed([{ isPending: false, data }]));
    }

    return data.id;
  } catch (error) {
    Sentry.captureException(error);
    Sentry.captureMessage("updateCase compensation: failed to create orphaned case");
    const exception: any = error;
    const event = exception?.response?.status === 401 ? ErrorEvents.ACCESS_DENIED : undefined;
    const message =
      event || exception?.response?.statusText || exception?.message || "Unknown error.";
    yield put(casesSlice.newCaseFailure({ event, message }));
    return 0;
  }
}

function* updateCase({ caseId, isAuth, updateBody }: casesActions.UpdateCase) {
  yield put(casesSlice.newCaseFailure({}));
  yield put(casesSlice.newCasePending());

  let effectiveCaseId = caseId;

  // Layer 2: Compensate when case was never created (caseId === 0).
  if (effectiveCaseId === 0) {
    const existingCase: Case = yield selectState<Case>((state) => state.case.newCase.data);
    effectiveCaseId = yield call(compensateOrphanedCase, existingCase);
    if (effectiveCaseId === 0) return;
  }

  const caseUpdateURL = `/${effectiveCaseId}${!isAuth ? "/orphan" : ""}`;
  // Re-select state after potential compensation to avoid stale data.
  const currentCase: Case = yield selectState<Case>((state) => state.case.newCase.data);

  let validatedAddress: Address | null = null;
  // Handle shipping address validation if present in the request.
  if (updateBody?.shippingAddress?.id) {
    const user: User = yield selectState<User>((state) => state.user.data);
    validatedAddress = getValidatedShippingAddress(updateBody.shippingAddress.id, user);
  }

  try {
    // Perform the main case update attempt.
    const { data } = yield call(() => cases.casePut(caseUpdateURL, updateBody));
    let updatedCase: Case = { ...data, ...updateBody };
    if (validatedAddress) {
      updatedCase = { ...updatedCase, shippingAddress: validatedAddress };
    }
    yield put(casesSlice.newCaseSuccess({ ...currentCase, ...updatedCase }));
    return;
  } catch (error) {
    const exception: any = error;
    const event = exception?.response?.status === 401 ? ErrorEvents.ACCESS_DENIED : undefined;
    const errorMessage = exception?.response?.statusText || exception?.message || "Unknown error.";

    // Attempt retry only for network errors.
    if (errorMessage === ErrorEvents.ERR_NETWORK) {
      try {
        // Retry 3 times with 3 second delay between attempts.
        const { data } = yield retry(3, 3000, cases.casePut, caseUpdateURL, updateBody);
        let updatedCase: Case = { ...data, ...updateBody };
        if (validatedAddress) {
          updatedCase = { ...updatedCase, shippingAddress: validatedAddress };
        }
        yield put(casesSlice.newCaseSuccess({ ...currentCase, ...updatedCase }));
        return;
      } catch (retryError) {
        // Retry failed, proceed to error handling below.
        Sentry.captureException(retryError);
      }
    }

    // Handle all errors (initial or retry failures) uniformly.
    yield put(
      casesSlice.newCaseFailure({
        event,
        message: errorMessage,
      })
    );
  }
}

function* createCaseInvoice({
  caseId,
  pricing,
  promoCodeId,
  customerId,
  redirects,
}: casesActions.CreateCaseInvoice) {
  // Get current case from Redux state
  const existingCase: Case = yield selectState<Case>((state) => state.case.newCase.data);
  yield put(casesSlice.newCaseFailure({}));
  yield put(casesSlice.newCasePending());

  try {
    // Create case invoice with provider-specific pricing
    const invoicePayload: InvoiceRequestPayload = {
      caseId,
      pricing: {
        priceId: pricing.priceId,
        productId: pricing.productId,
        provider:
          pricing.provider === PAYMENT_PROVIDER_WITHFLEX
            ? PAYMENT_PROVIDER_WITHFLEX
            : PAYMENT_PROVIDER_STRIPE,
      },
      promoCodeId,
      ...(customerId && { customerId }),
      ...(redirects && {
        successUrl: redirects.successUrl,
        cancelUrl: redirects.cancelUrl,
      }),
    };

    const {
      data: { invoice },
    } = yield call(() => cases.casePost("/invoice", invoicePayload));
    yield put(casesSlice.newCaseSuccess({ ...existingCase, paid: invoice.paid, invoice }));
  } catch (error) {
    Sentry.captureException(error);
    yield put(
      casesSlice.newCaseFailure({
        message: ErrorEvents.FAILED_INVOICE,
      })
    );
  }
}

export function* payCase({ newCaseId, paymentMethods, promoCodeId, manual }: casesActions.PayCase) {
  if (manual) {
    if (paymentMethods.length === 0) {
      // Dedicated actions, not generic userMutationSuccess/userError, which also fire for unrelated things on this page.
      const paymentMethodResult: { type: string } = yield take([
        userActions.ADD_PAYMENT_METHOD_SUCCEEDED,
        userActions.ADD_PAYMENT_METHOD_FAILED,
      ]);

      if (paymentMethodResult.type === userActions.ADD_PAYMENT_METHOD_FAILED) {
        // addPaymentMethod (userSaga.ts) already toasted this failure — don't compound it.
        yield put(
          casesSlice.newCaseFailure({
            event: ErrorEvents.ADD_PAYMENT_METHOD,
            message: ErrorEvents.ADD_PAYMENT_METHOD,
          })
        );
        return;
      }
    }

    // Initiate loaders.
    yield put(casesSlice.newCaseFailure({}));
    yield put(casesSlice.newCasePending());

    // Create invoice if it doesn't exist (idempotent - saga handles check)
    // Get user from Redux state to access Stripe customer ID
    const user: User = yield selectState<User>((state) => state.user.data);

    yield call(createCaseInvoice, {
      type: casesActions.CREATE_CASE_INVOICE,
      caseId: newCaseId,
      pricing: {
        priceId: VISIT_FEE_STRIPE_PRICE_ID!,
        productId: VISIT_FEE_STRIPE_PRODUCT_ID!,
        provider: PAYMENT_PROVIDER_STRIPE,
      },
      promoCodeId,
      customerId: user.customer_id,
    });

    // Check if invoice creation failed (check error state from RootEntity)
    const newCaseState: RootEntity<Case> = yield selectState<RootEntity<Case>>(
      (state) => state.case.newCase
    );
    if (newCaseState.error?.message === ErrorEvents.FAILED_INVOICE) {
      yield put(push("/thank_you"));
      return;
    }
  }

  try {
    // We return the pending state after successfully creating the invoice.
    yield put(casesSlice.newCasePending());
    // Pay the case.
    const { data } = yield call(() =>
      cases.casePost("/pay", {
        caseId: newCaseId,
      })
    );
    // Then succeed the new case.
    yield put(casesSlice.newCaseSuccess(data.case));
    yield put(push("/thank_you"));
  } catch (error) {
    Sentry.captureException(error);
    Sentry.captureMessage(`Payment failed for case ${newCaseId}`);

    const exception: any = error;
    const genericErrorMessage = "Payment processing failed. Please try again.";
    const serverErrorMessage = exception?.response?.data?.message || exception?.message;
    const errorMessage = serverErrorMessage?.includes("declined")
      ? serverErrorMessage
      : genericErrorMessage;

    let event: string | undefined;

    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }

    yield put(
      casesSlice.newCaseFailure({
        event,
        message: errorMessage,
      })
    );
  }
}

function* sendCase({ newCase, isPlus }: casesActions.SendCase) {
  // Check if already paid, otherwise wait a case mutation.
  const currentCase: Case = yield selectState<Case>((state) => state.case.newCase.data);
  if (!currentCase.paid && !currentCase.invoice?.paid) {
    yield take((action: any) => {
      return (
        // Check if the action is a newCaseSuccess or newCaseFailure and the id matches.
        (action.type === casesSlice.newCaseSuccess.type ||
          action.type === casesSlice.newCaseFailure.type) &&
        action.payload?.id === newCase.id
      );
    });
  }

  try {
    // Submit lab exam requirements before sending the case to the telehealth vendor.
    const labExamRequirements = extractLabExamRequirements(newCase.conditions);
    if (labExamRequirements.length > 0) {
      try {
        yield call(() =>
          labExam.labExamPost("/submit", {
            caseId: newCase.id,
            labExamRequirements,
          })
        );
      } catch (labExamError) {
        const labException: any = labExamError;
        // 409 means already submitted — safe to continue to /send.
        if (labException?.response?.status !== 409) {
          Sentry.captureException(labExamError);
          return;
        }
      }
    }

    const { data } = yield call(() =>
      cases.casePost("/send", {
        caseId: newCase.id,
        isPlus,
      })
    );
    yield put(
      casesSlice.updateCaseSuccess({
        caseId: newCase.id,
        data,
      })
    );
  } catch (error) {
    Sentry.captureException(error);
  }
}

function* requestCases({ userId }: casesActions.RequestCases) {
  yield put(casesSlice.getCasesFailure({}));
  yield put(casesSlice.getCasesPending());
  try {
    const { data } = yield call(() => user.userGet(`${userId}/cases`));
    yield put(casesSlice.getCasesSuccess(data));
  } catch (error) {
    const exception: any = error;
    const event = exception?.response?.status === 401 ? ErrorEvents.ACCESS_DENIED : undefined;
    const message = event || exception?.response?.statusText || "Unknown error.";

    yield put(
      casesSlice.getCasesFailure({
        event,
        message,
      })
    );
  }
}

function* refreshCases({ userId }: casesActions.RefreshCases) {
  yield put(casesSlice.getCasesFailure({}));
  yield put(casesSlice.getCasesPending());
  try {
    const { data } = yield call(() => cases.casePut(`/telehealth/sync?userId=${userId}`));
    yield put(casesSlice.getCasesSuccess(data));
  } catch (error) {
    const exception: any = error;
    const event = exception?.response?.status === 401 ? ErrorEvents.ACCESS_DENIED : undefined;
    const message = event || exception?.response?.statusText || "Unknown error.";

    yield put(
      casesSlice.getCasesFailure({
        event,
        message,
      })
    );
  }
}

export function* requestOrderInvoice({ orderId, caseId }: casesActions.RequestOrderInvoice) {
  try {
    const { data } = yield call(() => order.orderGet(`/${orderId}/invoice`));
    // An order without an invoice answers null.
    yield put(
      data
        ? casesSlice.getInvoiceSuccess({ caseId, data })
        : casesSlice.getInvoiceUnavailable({ caseId })
    );
  } catch (error) {
    Sentry.captureException(error);
    Sentry.captureMessage(`Cannot load invoice for order ${orderId}`);
    yield put(casesSlice.getInvoiceUnavailable({ caseId }));
  }
}

const requestOrderSend = (orderId: string) => call(order.orderPut, `/${orderId}/send`);

const failedPaymentOutcome = (error: PaymentRetryError): PaymentAttemptOutcome => ({
  status: PaymentAttemptStatus.FAILED,
  error,
});

// A refusal or an ended session is an expected outcome, not an error to report.
const isUnexpectedFailure = ({ kind }: PaymentRetryError) =>
  kind !== PaymentRetryErrorKind.PAYMENT_REFUSED && kind !== PaymentRetryErrorKind.SESSION_ENDED;

function reportPaymentFailure(failure: unknown, message: string) {
  Sentry.captureException(failure);
  Sentry.captureMessage(message);
}

function* attemptOrderPayment(
  orderId: string,
  invoiceProvider: casesActions.PayOrder["invoiceProvider"]
) {
  let paymentResponse: { data?: { order?: { paid?: boolean } } };
  try {
    if (invoiceProvider) {
      yield call(order.orderPost, "/invoice", { orderId, paymentProvider: invoiceProvider });
    }
    paymentResponse = yield call(order.orderPut, `/${orderId}/pay`);
  } catch (failure) {
    const error = classifyPaymentRetryError(failure);
    if (isUnexpectedFailure(error)) {
      yield call(reportPaymentFailure, failure, `User cannot pay order ${orderId}`);
    }
    return failedPaymentOutcome(error);
  }

  if (paymentResponse?.data?.order?.paid !== true) {
    return failedPaymentOutcome({ kind: PaymentRetryErrorKind.UNKNOWN, isRetryable: true });
  }

  try {
    yield requestOrderSend(orderId);
  } catch (failure) {
    yield call(reportPaymentFailure, failure, `User cannot send order ${orderId}`);
    return failedPaymentOutcome({ kind: PaymentRetryErrorKind.SEND_FAILED, isRetryable: false });
  }

  return { status: PaymentAttemptStatus.SUCCEEDED };
}

// A card payment reviews an unpaid Stripe invoice that is the order's own, never the snapshot of a
// declined HSA/FSA charge.
const isReviewableCardInvoice = (invoice: Case["CurexaOrder"]["invoice"]) =>
  invoice?.provider === PAYMENT_PROVIDER_STRIPE &&
  invoice.paid === false &&
  invoice.metadata?.isEstimate !== true;

// Creating a Stripe invoice charges nothing, so any failure here leaves the patient uncharged.
const classifyPreparationError = (failure: unknown): PaymentRetryError => {
  const error = classifyPaymentRetryError(failure);
  return error.kind === PaymentRetryErrorKind.SESSION_ENDED
    ? error
    : { kind: PaymentRetryErrorKind.NOT_STARTED, isRetryable: true };
};

const selectCase = (caseId: number) =>
  selectState<RootEntity<Case> | undefined>((state) =>
    state.case.cases.data.find((case_) => case_.data.id === caseId)
  );

export function* prepareCardPayment({ orderId, caseId }: casesActions.PrepareCardPayment) {
  const case_: RootEntity<Case> | undefined = yield selectCase(caseId);
  if (!case_ || case_.isPending) {
    return;
  }

  yield put(casesSlice.caseMutationFailure({ caseId, error: {} }));
  yield put(casesSlice.caseMutationPending({ caseId }));

  if (!isReviewableCardInvoice(case_.data.CurexaOrder.invoice)) {
    try {
      yield call(order.orderPost, "/invoice", {
        orderId,
        paymentProvider: PAYMENT_PROVIDER_STRIPE,
      });
    } catch (failure) {
      const error = classifyPreparationError(failure);
      if (error.kind !== PaymentRetryErrorKind.SESSION_ENDED) {
        yield call(
          reportPaymentFailure,
          failure,
          `User cannot start a card payment for order ${orderId}`
        );
      }
      yield put(
        casesSlice.paymentAttemptRecorded({ caseId, outcome: failedPaymentOutcome(error) })
      );
      return;
    }

    yield call(requestOrderInvoice, {
      type: casesActions.REQUEST_ORDER_INVOICE,
      orderId: case_.data.CurexaOrder.id,
      caseId,
    });
    const prepared: RootEntity<Case> | undefined = yield selectCase(caseId);
    if (!isReviewableCardInvoice(prepared?.data.CurexaOrder.invoice)) {
      yield put(
        casesSlice.paymentAttemptRecorded({
          caseId,
          outcome: failedPaymentOutcome({
            kind: PaymentRetryErrorKind.NOT_STARTED,
            isRetryable: true,
          }),
        })
      );
      return;
    }
  }

  yield put(casesSlice.cardPaymentReviewOpened({ caseId }));
}

function* rereadCases() {
  const userId: string = yield selectState<string>((state) => state.user.data.id);
  yield call(requestCases, { type: casesActions.REQUEST_CASES, userId });
}

const isSessionEnded = (outcome: PaymentAttemptOutcome) =>
  outcome.status === PaymentAttemptStatus.FAILED &&
  outcome.error.kind === PaymentRetryErrorKind.SESSION_ENDED;

export function* payOrder({ orderId, caseId, invoiceProvider }: casesActions.PayOrder) {
  const isAttemptInProgress: boolean = yield selectState<boolean>(
    (state) => state.case.cases.data.find((case_) => case_.data.id === caseId)?.isPending ?? false
  );
  if (isAttemptInProgress) {
    return;
  }

  yield put(casesSlice.caseMutationFailure({ caseId, error: {} }));
  yield put(casesSlice.caseMutationPending({ caseId }));

  const outcome: PaymentAttemptOutcome = yield call(attemptOrderPayment, orderId, invoiceProvider);
  if (!isSessionEnded(outcome)) {
    yield call(rereadCases);
  }
  yield put(casesSlice.paymentAttemptRecorded({ caseId, outcome }));
}

export default function* caseSaga() {
  yield takeEvery(casesActions.CREATE_CASE, createCase);
  yield takeEvery(casesActions.UPDATE_CASE, updateCase);
  yield takeEvery(casesActions.CREATE_CASE_INVOICE, createCaseInvoice);
  yield takeEvery(casesActions.PAY_CASE, payCase);
  yield takeEvery(casesActions.SEND_CASE, sendCase);
  yield takeEvery(casesActions.REQUEST_CASES, requestCases);
  yield takeEvery(casesActions.REFRESH_CASES, refreshCases);
  yield takeEvery(casesActions.REQUEST_ORDER_INVOICE, requestOrderInvoice);
  yield takeEvery(casesActions.PAY_ORDER, payOrder);
  yield takeEvery(casesActions.PREPARE_CARD_PAYMENT, prepareCardPayment);
}
